import { useState, useCallback, useRef, useEffect, type RefObject } from 'react';
import type { DragPosition } from './types';

interface UseDragOptions {
  initialPosition?: DragPosition;
  elementRef: RefObject<HTMLElement | null>;
  /** Fired once per drag, on pointer-up, with the final position — the hook
   *  for persisting the spot (e.g. into server-backed uiPrefs). */
  onDragEnd?: (pos: DragPosition) => void;
}

interface UseDragResult {
  position: DragPosition;
  handlePointerDown: (e: React.PointerEvent) => void;
  isDragging: boolean;
  /** Externally reposition (viewport-clamped) — used to restore a persisted
   *  position once async prefs arrive. */
  setPosition: (pos: DragPosition) => void;
}

function clampToViewport(x: number, y: number, w: number, h: number): DragPosition {
  return {
    x: Math.max(0, Math.min(x, window.innerWidth - Math.min(w, window.innerWidth))),
    y: Math.max(0, Math.min(y, window.innerHeight - Math.min(h, window.innerHeight))),
  };
}

export function useDrag({ initialPosition, elementRef, onDragEnd }: UseDragOptions): UseDragResult {
  const [position, setPosition] = useState<DragPosition>(
    initialPosition ?? { x: window.innerWidth - 216, y: window.innerHeight - 96 },
  );
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(null);
  // Ref so the pointer-up closure sees the latest callback and position
  // without re-binding listeners mid-drag.
  const onDragEndRef = useRef(onDragEnd);
  onDragEndRef.current = onDragEnd;
  const positionRef = useRef(position);
  positionRef.current = position;
  // The spot the user chose (a drag, a restored position). `position` is that
  // spot clamped into the current window and HUD size, so a window that
  // shrinks and grows back returns the HUD to the user's spot instead of
  // leaving it where the clamp pushed it (GM Studio 5.2).
  const chosenRef = useRef(position);

  const setPositionClamped = useCallback((pos: DragPosition) => {
    const el = elementRef.current;
    chosenRef.current = pos;
    setPosition(clampToViewport(pos.x, pos.y, el?.offsetWidth ?? 200, el?.offsetHeight ?? 40));
  }, [elementRef]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // The drag handle may host interactive controls (collapse / expand /
    // open-full-sheet buttons) — bail out when the press starts on one so its
    // click goes through. The handle ITSELF may be interactive too (the compact
    // pill is a role="button" that expands on click), so only bail for controls
    // NESTED inside the handle, not for the handle element.
    const interactive = (e.target as HTMLElement).closest('button, a, input, select, textarea, [role="button"]');
    if (interactive && interactive !== e.currentTarget) {
      return;
    }
    // Primary button / first touch only — a right-click or a second finger
    // must not start (or teleport) a drag.
    if (e.button !== 0) return;
    const pointerId = e.pointerId;
    const target = e.currentTarget as HTMLElement;
    dragStart.current = {
      startX: e.clientX,
      startY: e.clientY,
      posX: position.x,
      posY: position.y,
    };

    // Do NOT capture/preventDefault yet: a press-and-release without movement
    // must stay a plain click (pill expand). Drag engages only past a small
    // movement threshold; before that the pointer is untouched.
    const DRAG_THRESHOLD_PX = 4;
    let engaged = false;

    const onPointerMove = (ev: PointerEvent) => {
      if (!dragStart.current) return;
      // Track ONLY the pointer that started the gesture (multitouch).
      if (ev.pointerId !== pointerId) return;
      const dx = ev.clientX - dragStart.current.startX;
      const dy = ev.clientY - dragStart.current.startY;
      if (!engaged) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
        engaged = true;
        try { target.setPointerCapture(e.pointerId); } catch { /* pointer may be gone */ }
        setIsDragging(true);
      }
      const el = elementRef.current;
      const w = el?.offsetWidth ?? 200;
      const h = el?.offsetHeight ?? 40;
      const clamped = clampToViewport(
        dragStart.current.posX + dx,
        dragStart.current.posY + dy,
        w,
        h,
      );
      chosenRef.current = clamped;
      setPosition(clamped);
    };

    const onPointerUp = (ev?: PointerEvent) => {
      if (ev && ev.pointerId !== pointerId) return; // another finger lifted
      const wasEngaged = engaged;
      dragStart.current = null;
      setIsDragging(false);
      if (wasEngaged) {
        try { target.releasePointerCapture(pointerId); } catch { /* already released */ }
        // The browser still fires a click after pointerup — on an interactive
        // handle (pill) that would expand the HUD at the end of every drag.
        // Swallow exactly that one click.
        const suppress = (ce: Event) => { ce.stopPropagation(); ce.preventDefault(); };
        target.addEventListener('click', suppress, { capture: true, once: true });
        setTimeout(() => target.removeEventListener('click', suppress, { capture: true }), 0);
      }
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      if (wasEngaged) onDragEndRef.current?.(positionRef.current);
    };

    // Listen on window so the pre-capture threshold phase tracks the pointer
    // even when it leaves the handle. pointercancel (OS gesture steals the
    // pointer, touch scroll, etc.) must tear down too — without it isDragging
    // stuck on and the listeners leaked.
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  }, [position.x, position.y, elementRef]);

  // Recalculate on window resize OR element size change (HUD state transition
  // grows the container — without this the new height can push the HUD off the
  // bottom of the viewport, making mini/combat invisible to the user).
  useEffect(() => {
    const clampNow = () => {
      const el = elementRef.current;
      if (!el) return;
      const chosen = chosenRef.current;
      setPosition(prev => {
        const clamped = clampToViewport(chosen.x, chosen.y, el.offsetWidth, el.offsetHeight);
        return (clamped.x === prev.x && clamped.y === prev.y) ? prev : clamped;
      });
    };
    window.addEventListener('resize', clampNow);

    const el = elementRef.current;
    let ro: ResizeObserver | null = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(clampNow);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', clampNow);
      ro?.disconnect();
    };
  }, [elementRef]);

  return { position, handlePointerDown, isDragging, setPosition: setPositionClamped };
}
